'use client';

import { useEffect, useId, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { CategoryIcon } from '@/components/CategoryIcon';
import { todayDateKey } from '@/lib/date';
import { Category, Tag, Transaction } from '@/lib/types';

export type TransactionFormPayload = {
  amount: number;
  type: 'income' | 'expense';
  date: string;
  categoryId?: string;
  tagIds: string[];
  tagNames: string[];
  note?: string;
};

type TransactionFormProps = {
  categories: Category[];
  tags: Tag[];
  initialValue?: Transaction | null;
  loading: boolean;
  onSave: (payload: TransactionFormPayload) => Promise<void>;
  onCancel: () => void;
};

export default function TransactionForm({ categories, tags, initialValue, loading, onSave, onCancel }: TransactionFormProps) {
  const amountId = useId();
  const errorId = useId();
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [date, setDate] = useState(todayDateKey());
  const [categoryId, setCategoryId] = useState('');
  const [tagText, setTagText] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAmount(initialValue?.amount.toString() ?? '');
    setType(initialValue?.type ?? 'expense');
    setDate(initialValue?.date ?? todayDateKey());
    setCategoryId(initialValue?.categoryId ?? '');
    setSelectedTagIds(initialValue?.tagIds ?? []);
    setNote(initialValue?.note ?? '');
    setTagText('');
    setError(null);
  }, [initialValue]);

  const availableTags = useMemo(
    () => tags.filter((tag) => !selectedTagIds.includes(tag.id)),
    [tags, selectedTagIds]
  );
  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === categoryId) ?? null,
    [categories, categoryId]
  );

  const resetForm = () => {
    setAmount('');
    setType('expense');
    setDate(todayDateKey());
    setCategoryId('');
    setSelectedTagIds([]);
    setNote('');
    setTagText('');
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedAmount = Number(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Indiquez un montant supérieur à zéro.');
      return;
    }

    await onSave({
      amount: parsedAmount,
      type,
      date,
      categoryId: categoryId || undefined,
      tagIds: selectedTagIds,
      tagNames: tagText
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      note: note.trim() || undefined,
    });
    resetForm();
  };

  const expenseTypeButtonClass =
    type === 'expense'
      ? 'bg-rose-500/15 text-rose-200 shadow-sm'
      : 'bg-transparent text-slate-400';
  const incomeTypeButtonClass =
    type === 'income'
      ? 'bg-emerald-500/15 text-emerald-200 shadow-sm'
      : 'bg-transparent text-slate-400';

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-describedby={error ? errorId : undefined}>
      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-950 p-1">
        <button
          type="button"
          onClick={() => setType('expense')}
          aria-pressed={type === 'expense'}
          className={`border-0 px-4 py-3 text-sm font-semibold shadow-none ${expenseTypeButtonClass}`}
        >
          Dépense
        </button>
        <button
          type="button"
          onClick={() => setType('income')}
          aria-pressed={type === 'income'}
          className={`border-0 px-4 py-3 text-sm font-semibold shadow-none ${incomeTypeButtonClass}`}
        >
          Entrée
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-200">Montant</span>
          <input
            id={amountId}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            className="mt-2 w-full px-4 py-3"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-200">Date</span>
          <input value={date} onChange={(e) => setDate(e.target.value)} type="date" className="mt-2 w-full px-4 py-3" />
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-200">Catégorie</span>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="mt-2 w-full px-4 py-3">
          <option value="">Aucune catégorie</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
        {selectedCategory ? (
          <span className="mt-2 flex items-center gap-2 text-sm text-slate-300">
            <CategoryIcon category={selectedCategory} className="h-8 w-8" />
            {selectedCategory.name}
          </span>
        ) : null}
      </label>

      <details className="rounded-2xl border border-slate-800 bg-slate-950 p-3 min-[380px]:p-4">
        <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold text-slate-200">Tags et note optionnels</summary>
        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-200">Tags</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedTagIds.map((tagId) => {
                const tag = tags.find((item) => item.id === tagId);
                if (!tag) return null;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id))}
                    aria-label={`Retirer le tag ${tag.name}`}
                    className="min-h-9 rounded-full border border-brand-500/30 bg-brand-500/15 px-3 py-1 text-xs font-semibold text-brand-200 shadow-none"
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
            <div className="mt-3">
              <input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="Marché, train, loyer..." className="w-full px-4 py-3" list="available-tags" />
              <datalist id="available-tags">
                {availableTags.map((tag) => (
                  <option key={tag.id} value={tag.name} />
                ))}
              </datalist>
              <p className="mt-2 text-xs text-slate-500">Séparez plusieurs tags par des virgules.</p>
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-200">Note</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Optionnel" className="mt-2 w-full px-4 py-3" />
          </label>
        </div>
      </details>

      {error ? (
        <p id={errorId} className="rounded-2xl bg-rose-950 p-3 text-sm text-rose-200" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="submit" disabled={loading} className="primary-button sm:flex-1">
          {initialValue ? 'Enregistrer les changements' : 'Ajouter'}
        </button>
        {initialValue ? (
          <button type="button" onClick={onCancel} className="secondary-button">
            Annuler
          </button>
        ) : null}
      </div>
    </form>
  );
}
